//app.js
import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Sidebar from './components/Sidebar.jsx';
import Header from './components/Header.jsx';
import Dashboard from './pages/Dashboard.jsx';
import BusinessRequirementGenerator from './pages/BusinessRequirementGenerator.jsx';
import DataModelGenerator from './pages/DataModelGenerator.jsx';
import SQLCopilot from './pages/SQLCopilot.jsx';
import RapidApplicationDev from './pages/RapidApplicationDev.jsx';
import DetApp from './pages/DetApp.jsx';
import ETL_Tool from './pages/ETL_Tool.jsx';
import ScriptGeneratorPage from './pages/ScriptGeneratorPage.jsx';
import {AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, MsalProvider} from '@azure/msal-react';
import { loginRequest } from './authconfig.js';
import Logout from './pages/Logout.jsx';
import api from './services/api.js';
import decLogo from './assets/dec-logo-icon.png';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error);
    console.error('Error info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Something went wrong.</h1>
          <p>Error: {this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

const microsoftIconStyles = `
.microsoft-icon {
    width: 20px;
    height: 20px;
    background: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjEiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMSAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjRjI1MDIyIi8+CjxyZWN0IHg9IjExIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiM3RkJBMDAiLz4KPHJlY3QgeT0iMTEiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzAwQTRFRiIvPgo8cmVjdCB4PSIxMSIgeT0iMTEiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI0ZGQjkwMCIvPgo8L3N2Zz4K) no-repeat center;
    background-size: contain;
    margin-right: 12px;
}
`;

// Function to capture user information from Microsoft Graph API
const captureUserInfo = async (msalInstance, account) => {
  try {
    // Get access token for Microsoft Graph
    const graphRequest = {
      scopes: ["User.Read", "User.ReadBasic.All"],
      account: account
    };
    
    const response = await msalInstance.acquireTokenSilent(graphRequest);
    
    // Fetch user profile from Microsoft Graph API
    const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${response.accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!graphResponse.ok) {
      throw new Error(`Graph API request failed: ${graphResponse.status}`);
    }
    
    const userData = await graphResponse.json();
    
    // Extract user information
    const userInfo = {
      displayName: userData.displayName || account.name || account.username,
      givenName: userData.givenName || '',
      surname: userData.surname || '',
      email: userData.mail || account.username,
      avatar: null
    };
    
    // Try to get user photo
    try {
      const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          'Authorization': `Bearer ${response.accessToken}`
        }
      });
      
      if (photoResponse.ok) {
        const photoBlob = await photoResponse.blob();
        const photoUrl = URL.createObjectURL(photoBlob);
        userInfo.avatar = photoUrl;
      }
    } catch (photoError) {
      console.log('User photo not available, using default avatar');
    }
    
    // Store user information in session storage
    sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
    
    return userInfo;
  } catch (error) {
    console.error('Error fetching user information:', error);
    // Fallback to basic account information
    const fallbackInfo = {
      displayName: account.name || account.username,
      givenName: '',
      surname: '',
      email: account.username,
      avatar: null
    };
    sessionStorage.setItem('userInfo', JSON.stringify(fallbackInfo));
    return fallbackInfo;
  }
};

const WrapperView = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true); // new state for desktop sidebar
  const [loginError, setLoginError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const { instance, accounts, inProgress } = useMsal();

  // Debug logging
  useEffect(() => {
    console.log('WrapperView mounted');
    console.log('MSAL instance:', instance);
    console.log('Accounts:', accounts);
    console.log('In progress:', inProgress);
  }, []);

  // Inject Microsoft icon styles
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = microsoftIconStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Handle authentication state changes
  useEffect(() => {
    console.log('Authentication state:', {
      accounts: accounts.length,
      inProgress: inProgress,
      activeAccount: !!instance.getActiveAccount()
    });

    // Set active account if we have accounts but no active account
    if (inProgress === 'none' && accounts.length > 0 && !instance.getActiveAccount()) {
      console.log('Setting active account:', accounts[0].username);
      instance.setActiveAccount(accounts[0]);
    }
    
    // Load user information from session storage
    const storedUserInfo = sessionStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        setUserInfo(parsedUserInfo);
        console.log('Loaded user info from session storage:', parsedUserInfo);
      } catch (error) {
        console.error('Error parsing stored user info:', error);
      }
    }
  }, [inProgress, accounts, instance]);

  const handleLogin = async () => {
    try {
      setLoginError(null);
      console.log('Starting login process...');
      
      // Try popup first for better user experience
      const response = await instance.loginPopup({
        ...loginRequest,
        prompt: 'select_account'
      });
      
      console.log('Login successful:', response.account.username);
      instance.setActiveAccount(response.account);
      
      // Capture user information after successful login
      try {
        const userInfo = await captureUserInfo(instance, response.account);
        setUserInfo(userInfo);
        console.log('User information captured after login:', userInfo);
        // --- Call backend login API ---
        if (userInfo && userInfo.displayName && userInfo.email) {
          api.loginUser({ username: userInfo.displayName, email: userInfo.email })
            .then(res => console.log('Login event logged:', res.data))
            .catch(err => console.error('Failed to log login event:', err));
        }
      } catch (error) {
        console.error('Error capturing user information after login:', error);
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Login failed. Please try again.');
      
      // Check for specific Azure AD errors
      if (error.errorCode === 'AADSTS9002326') {
        setLoginError('Azure AD configuration error: App must be configured as Single-Page Application (SPA). Please contact your administrator.');
      } else if (error.errorCode === 'AADSTS50020') {
        setLoginError('User account not found in the directory. Please contact your administrator.');
      } else if (error.errorCode === 'AADSTS50105') {
        setLoginError('The signed in user is not assigned to a role for the application. Please contact your administrator.');
      } else if (error.errorCode === 'AADSTS65001') {
        setLoginError('The user or administrator has not consented to use the application. Please contact your administrator.');
      }
      
      console.log('Popup login failed, trying redirect:', error);
      
      // Fallback to redirect if popup fails
      try {
        await instance.loginRedirect({
          ...loginRequest,
          prompt: 'select_account'
        });
      } catch (redirectError) {
        console.error('Login redirect failed:', redirectError);
        setLoginError('Both popup and redirect login methods failed. Please try refreshing the page.');
      }
    }
  };

  const handleLogout = () => {
    // --- Call backend logout API ---
    const storedUserInfo = sessionStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        if (parsedUserInfo.displayName && parsedUserInfo.email) {
          api.logoutUser({ username: parsedUserInfo.displayName, email: parsedUserInfo.email })
            .then(res => console.log('Logout event logged:', res.data))
            .catch(err => console.error('Failed to log logout event:', err));
        }
      } catch (error) {
        console.error('Error parsing user info for logout event:', error);
      }
    }
    // Clear user information from session storage
    sessionStorage.removeItem('userInfo');
    setUserInfo(null);
    window.location.href = '/logout';
    
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/logout'
      });
  
  };

  return (
    <ErrorBoundary>
      <div className='App'>
        <AuthenticatedTemplate>
          <Routes>
            <Route path="/logout" element={<Logout />} />
            <Route path="*" element={
              <div className="h-screen w-screen overflow-hidden bg-surfaceMuted">
                <Header
                  desktopSidebarOpen={desktopSidebarOpen}
                  onToggleDesktopSidebar={() => setDesktopSidebarOpen((prev) => !prev)}
                  onOpenMobileSidebar={() => setSidebarOpen(true)}
                  onLogout={handleLogout}
                  userInfo={userInfo}
                />

                <div className="flex overflow-hidden" style={{ marginTop: '64px', height: 'calc(100vh - 64px)' }}>
                  <Sidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    desktopSidebarOpen={desktopSidebarOpen}
                  />

                  <main className="flex-1 overflow-y-auto overflow-x-hidden app-main-scroll bg-surfaceMuted p-4 md:p-6">
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/business-requirement-generator" element={<BusinessRequirementGenerator />} />
                      <Route path="/data-model-generator" element={<DataModelGenerator />} />
                      <Route path="/sql-copilot" element={<SQLCopilot />} />
                      <Route path="/rapid-application-dev" element={<RapidApplicationDev />} />
                      <Route path="/detapp" element={<DetApp />} />
                      <Route path="/etl-tool" element={<ETL_Tool />} />
                      <Route path="/script-generator" element={<ScriptGeneratorPage />} />
                    </Routes>
                  </main>
                </div>
              </div>
            } />
          </Routes>
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <Routes>
            <Route path="/logout" element={<Logout />} />
            <Route path="*" element={
              <div className="fixed inset-0 flex flex-col items-center justify-center p-4 bg-surfaceMuted overflow-y-auto">
                {/* Login card — spec §23 */}
                <div className="bg-white rounded-[24px] shadow-shadowAccess p-8 w-full max-w-[400px] text-center">
                  <img src={decLogo} alt="" className="h-20 w-auto object-contain mx-auto mb-4" />

                  <h1 className="text-textPrimary font-bold" style={{ fontSize: '1.5rem' }}>
                    Data Modeling Copilot
                  </h1>
                  <p className="text-textSecondary mb-6" style={{ fontSize: '0.875rem' }}>
                    Please sign in to continue
                  </p>

                  {loginError && (
                    <div className="text-left bg-red-50 border border-red-200 rounded-radiusSm p-3 mb-4">
                      <p className="text-sm text-red-800">{loginError}</p>
                    </div>
                  )}

                  <button
                    onClick={handleLogin}
                    disabled={inProgress !== 'none'}
                    className="w-full flex items-center justify-center py-3 px-4 rounded-radiusSm text-sm font-semibold text-white bg-actionPrimary hover:bg-actionPrimaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  >
                    <div className="microsoft-icon"></div>
                    {inProgress !== 'none' ? 'Signing in...' : 'Sign in with Microsoft'}
                  </button>

                  {inProgress !== 'none' && (
                    <p className="text-textSecondary mt-3" style={{ fontSize: '0.8rem' }}>
                      Authentication in progress... Please wait.
                    </p>
                  )}
                </div>

                {/* Copyright statement */}
                <div className="mt-8 text-center">
                  <p className="text-textSecondary" style={{ fontSize: '0.8rem' }}>
                    © {new Date().getFullYear()} Bigtapp Analytics. All rights reserved.
                  </p>
                </div>
              </div>
            } />
          </Routes>
        </UnauthenticatedTemplate>
      </div>
    </ErrorBoundary>
  );
};

const App = ({ Instance }) => {
  console.log('App component rendered with Instance:', Instance);
  
  if (!Instance) {
    console.error('MSAL Instance is undefined!');
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Authentication Error</h1>
        <p>MSAL instance is not available. Please refresh the page.</p>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <MsalProvider instance={Instance}>
        <WrapperView />
      </MsalProvider>
    </ErrorBoundary>
  );
};

export default App;